// Browser-based Git service using wasm-git (libgit2 compiled to WASM)
// All heavy WASM work runs in a Web Worker to keep the UI responsive.
// Communicates with git-worker.ts via postMessage.

import type {
  RepoConfig,
  Commit,
  AuthorStats,
  BranchInfo,
  DailyActivity,
  RepoStats,
  ProgressCallback,
} from "./types";

// ==================== Worker communication ====================

let workerInstance: Worker | null = null;
let msgIdCounter = 0;
const pendingRequests = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();
let globalProgressCb: ((msg: string) => void) | null = null;

function getWorker(): Worker {
  if (workerInstance) return workerInstance;
  workerInstance = new Worker(
    new URL("./git-worker.ts", import.meta.url),
    { type: "module" }
  );
  workerInstance.onmessage = (e: MessageEvent) => {
    const { id, type, result, error, message } = e.data;

    if (type === "progress") {
      if (globalProgressCb) globalProgressCb(message);
      return;
    }

    const pending = pendingRequests.get(id);
    if (!pending) return;
    pendingRequests.delete(id);

    if (type === "error") {
      pending.reject(new Error(error));
    } else {
      pending.resolve(result);
    }
  };
  workerInstance.onerror = (e) => {
    console.error("[git-worker] Error:", e);
  };
  return workerInstance;
}

function workerRPC<T>(type: string, payload: Record<string, unknown>): Promise<T> {
  const worker = getWorker();
  const id = ++msgIdCounter;
  return new Promise<T>((resolve, reject) => {
    pendingRequests.set(id, {
      resolve: resolve as (v: unknown) => void,
      reject,
    });
    worker.postMessage({ id, type, payload });
  });
}

// ==================== URL helpers ====================

const DEV_PROXY = "http://localhost:3456";
const isDev = typeof window !== "undefined" && (window.location.port === "5173" || window.location.hostname === "localhost");

function getGitProxyBase(): string {
  if (isDev) return `${DEV_PROXY}/__git/`;
  // Must return a full https:// URL — libgit2 only activates HTTP transport for http(s):// URLs
  return `${typeof window !== "undefined" ? window.location.origin : ""}/__git/`;
}

function buildProxiedUrl(config: RepoConfig): string {
  let url = config.url || "";

  if (url.startsWith("git@github.com:")) {
    url = url.replace("git@github.com:", "https://github.com/");
  } else if (url.startsWith("git@bitbucket.org:")) {
    url = url.replace("git@bitbucket.org:", "https://bitbucket.org/");
  }

  url = url.replace(/https:\/\/[^@]+@/, "https://");

  if (!url.startsWith("https://")) {
    if (url.includes("github.com")) {
      url = "https://github.com/" + url.split("github.com/").pop();
    } else if (url.includes("bitbucket.org")) {
      url = "https://bitbucket.org/" + url.split("bitbucket.org/").pop();
    }
  }

  url = url.replace(/\/$/, "");
  if (!url.endsWith(".git")) url += ".git";

  if (config.accessToken) {
    url = url.replace("https://", `https://x-access-token:${config.accessToken}@`);
  } else if (config.username && config.appPassword) {
    url = url.replace("https://", `https://${encodeURIComponent(config.username)}:${encodeURIComponent(config.appPassword)}@`);
  }

  return `${getGitProxyBase()}${url}`;
}

function getRepoDir(config: RepoConfig): string {
  const identifier = config.url || config.path || config.name || "unknown";
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    const char = identifier.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  const safeName = identifier.replace(/[^a-zA-Z0-9-_]/g, "_").slice(0, 50);
  return `/repos/${safeName}_${Math.abs(hash).toString(36)}`;
}

// ==================== Main service class ====================

export interface GitServiceOptions {
  config: RepoConfig;
  progressCallback?: ProgressCallback;
}

export class BrowserGitService {
  private dir: string;
  private config: RepoConfig;
  private progressCallback?: ProgressCallback;

  constructor(options: GitServiceOptions) {
    this.config = options.config;
    this.progressCallback = options.progressCallback;
    this.dir = getRepoDir(options.config);
  }

  private log(message: string): void {
    console.log(message);
    if (this.progressCallback) {
      this.progressCallback(message);
    }
  }

  // Initialize: clone or fetch (runs entirely in worker)
  async init(): Promise<void> {
    const proxyUrl = buildProxiedUrl(this.config);

    globalProgressCb = (msg) => this.log(msg);
    try {
      await workerRPC("cloneOrFetch", { dir: this.dir, proxyUrl });
    } finally {
      globalProgressCb = null;
    }
  }

  async fetchLatest(): Promise<void> {
    const proxyUrl = buildProxiedUrl(this.config);
    globalProgressCb = (msg) => this.log(msg);
    try {
      await workerRPC("cloneOrFetch", { dir: this.dir, proxyUrl });
    } finally {
      globalProgressCb = null;
    }
  }

  async getBranches(
    weeks: number = 4,
    startDate?: string,
    endDate?: string
  ): Promise<BranchInfo[]> {
    const sinceDate = startDate
      ? new Date(startDate)
      : new Date(Date.now() - weeks * 7 * 24 * 60 * 60 * 1000);
    const untilDate = endDate ? new Date(endDate) : new Date();

    const raw = await workerRPC<Array<{
      name: string;
      isRemote: boolean;
      isHead: boolean;
      tipHash: string;
      lastAuthor: string;
      lastDate: string;
      lastMessage: string;
      commitCount: number;
      recentCommits: Array<{
        hash: string;
        author: string;
        email: string;
        date: string;
        message: string;
      }>;
    }>>(
      "getBranches",
      {
        dir: this.dir,
        since: sinceDate.toISOString(),
        until: untilDate.toISOString(),
      }
    );
    return raw.map((b) => ({
      name: b.name,
      isRemote: b.isRemote,
      lastCommit: b.lastDate,
      lastAuthor: b.lastAuthor,
      commitCount: b.commitCount,
      recentCommits: (b.recentCommits || []).map((c) => ({
        hash: c.hash,
        author: c.author,
        email: c.email,
        date: c.date,
        message: c.message,
      })),
    }));
  }

  async getCommits(
    weeks: number = 4,
    startDate?: string,
    endDate?: string
  ): Promise<Commit[]> {
    const sinceDate = startDate
      ? new Date(startDate)
      : new Date(Date.now() - weeks * 7 * 24 * 60 * 60 * 1000);

    const untilDate = endDate ? new Date(endDate) : new Date();
    untilDate.setDate(untilDate.getDate() + 1);

    this.log(`[${new Date().toISOString()}] Reading commits from all branches...`);

    globalProgressCb = (msg) => this.log(msg);
    try {
      const rawCommits = await workerRPC<Array<{
        hash: string;
        author: string;
        email: string;
        date: string;
        message: string;
        isMerge: boolean;
        additions: number;
        deletions: number;
        branch: string;
      }>>("getCommits", {
        dir: this.dir,
        since: sinceDate.toISOString(),
        until: untilDate.toISOString(),
      });

      this.log(`[${new Date().toISOString()}] Processed ${rawCommits.length} commits`);

      return rawCommits
        .map((c) => ({
          hash: c.hash,
          author: c.author,
          email: c.email,
          date: c.date,
          message: c.message,
          branch: c.branch,
          additions: c.additions,
          deletions: c.deletions,
          isMerge: c.isMerge,
        }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    } catch (e) {
      console.error("[wasm-git] Failed to read commits:", e);
      return [];
    } finally {
      globalProgressCb = null;
    }
  }

  async readFile(filename: string): Promise<string | null> {
    try {
      return await workerRPC<string | null>("readFile", { dir: this.dir, filename });
    } catch {
      return null;
    }
  }

  async getCommitDiff(hash: string): Promise<string> {
    try {
      return await workerRPC<string>("getDiff", { dir: this.dir, hash });
    } catch (e: unknown) {
      const msg = typeof e === "string" ? e : (e as Error)?.message || String(e);
      return `Error loading diff: ${msg}`;
    }
  }

  getStats(
    weeks: number = 4,
    prefetchedCommits?: Commit[],
    startDate?: string,
    endDate?: string
  ): RepoStats {
    const commits = prefetchedCommits || [];
    return this.computeStats(commits, weeks, startDate, endDate);
  }

  async getStatsAsync(
    weeks: number = 4,
    startDate?: string,
    endDate?: string
  ): Promise<RepoStats> {
    const commits = await this.getCommits(weeks, startDate, endDate);
    const branches = await this.getBranches(weeks, startDate, endDate);
    const stats = this.computeStats(commits, weeks, startDate, endDate);
    stats.branches = branches;
    stats.totalBranches = branches.length;
    return stats;
  }

  private computeStats(
    commits: Commit[],
    weeks: number,
    startDate?: string,
    endDate?: string
  ): RepoStats {
    const authorMap = new Map<string, AuthorStats>();
    const dailyMap = new Map<string, DailyActivity>();

    for (const commit of commits) {
      if (!authorMap.has(commit.author)) {
        authorMap.set(commit.author, {
          name: commit.author,
          email: commit.email,
          commits: 0,
          additions: 0,
          deletions: 0,
          firstCommit: commit.date,
          lastCommit: commit.date,
          activeDays: 0,
          avgCommitsPerDay: 0,
        });
      }

      const authorStats = authorMap.get(commit.author)!;
      authorStats.commits++;
      authorStats.additions += commit.additions;
      authorStats.deletions += commit.deletions;

      if (new Date(commit.date) < new Date(authorStats.firstCommit)) {
        authorStats.firstCommit = commit.date;
      }
      if (new Date(commit.date) > new Date(authorStats.lastCommit)) {
        authorStats.lastCommit = commit.date;
      }

      const dateKey = commit.date.split("T")[0];
      if (!dailyMap.has(dateKey)) {
        dailyMap.set(dateKey, {
          date: dateKey,
          commits: 0,
          additions: 0,
          deletions: 0,
          authors: [],
        });
      }

      const daily = dailyMap.get(dateKey)!;
      daily.commits++;
      daily.additions += commit.additions;
      daily.deletions += commit.deletions;
      if (!daily.authors.includes(commit.author)) {
        daily.authors.push(commit.author);
      }
    }

    const authorDays = new Map<string, Set<string>>();
    for (const commit of commits) {
      const dateKey = commit.date.split("T")[0];
      if (!authorDays.has(commit.author)) {
        authorDays.set(commit.author, new Set());
      }
      authorDays.get(commit.author)!.add(dateKey);
    }

    for (const [author, days] of authorDays) {
      const stats = authorMap.get(author)!;
      stats.activeDays = days.size;
      stats.avgCommitsPerDay =
        stats.activeDays > 0
          ? Math.round((stats.commits / stats.activeDays) * 10) / 10
          : 0;
    }

    const authors = Array.from(authorMap.values()).sort(
      (a, b) => b.commits - a.commits
    );
    const dailyActivity = Array.from(dailyMap.values()).sort((a, b) =>
      a.date.localeCompare(b.date)
    );

    const totalAdditions = authors.reduce((sum, a) => sum + a.additions, 0);
    const totalDeletions = authors.reduce((sum, a) => sum + a.deletions, 0);

    const rangeEnd = endDate ? new Date(endDate) : new Date();
    const rangeStart = startDate
      ? new Date(startDate)
      : new Date(rangeEnd.getTime() - weeks * 7 * 24 * 60 * 60 * 1000);

    return {
      totalCommits: commits.length,
      totalAuthors: authors.length,
      totalBranches: 0,
      totalAdditions,
      totalDeletions,
      authors,
      dailyActivity,
      branches: [],
      dateRange: {
        start: rangeStart.toISOString(),
        end: rangeEnd.toISOString(),
      },
    };
  }

  async cleanup(): Promise<void> {
    this.log(`Cleanup requested for: ${this.dir}`);
  }

  getRepoDir(): string {
    return this.dir;
  }
}

// List all repos currently cached (not yet available via worker — stub)
export async function listCachedRepos(): Promise<string[]> {
  return [];
}

// Delete a cached repo (not yet available via worker — stub)
export async function deleteCachedRepo(_name: string): Promise<void> {
  // TODO: add worker command for repo deletion
}
